import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Statistic, Empty, DatePicker, Switch, Alert,
  Typography, Divider, Tag, Card, Button, Select, Tooltip, message
} from 'antd';
import {
  HeartOutlined, SafetyCertificateOutlined, DollarOutlined, RiseOutlined,
  TeamOutlined, FallOutlined, MedicineBoxOutlined,
  ThunderboltOutlined, SettingOutlined, FilePdfOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import HeartLoader from '../../../components/shared/HeartLoader';
import { usePageHeader } from '../../../layouts/AdminLayout';
import api from '../../../services/api';
import ImpactSettingsDrawer from './SettingsDrawer';
import {
  PageContainer, FiltersContainer, ChartCard, SectionTitle
} from '../styled';

const { RangePicker } = DatePicker;
const { Title, Paragraph, Text } = Typography;

// Public-access NIH author manuscript of the source study (served from /public).
const STUDY_PDF_URL = `${process.env.PUBLIC_URL || ''}/nihms569917.pdf`;

// Black Heart brand palette — black + tiffany blue (no red).
const PALETTE = {
  ink: '#1a1a1a',
  charcoal: '#2d2d2d',
  slate: '#595959',
  tiffany: '#4ECDC4',
  brightTiffany: '#5AD6CE',
  deepTiffany: '#2E8B8B',
  tiffanyTint: '#E6FAF8',
  green: '#108043',
  heroGradient: 'linear-gradient(135deg, #000000 0%, #161616 55%, #232323 100%)',
};

const usd = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0
  );

const num = (n, d = 0) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: d }).format(Number.isFinite(n) ? n : 0);

const CARE_LABELS = {
  emergencyRoom: 'Emergency room',
  urgentCare: 'Urgent care',
  primaryCare: 'Primary care doctor',
  retailClinic: 'Retail / pharmacy clinic',
  nowhere: 'Would not have sought care',
  other: 'Other',
};

const StepCard = ({ index, title, children, accent = PALETTE.deepTiffany }) => (
  <Card
    bordered={false}
    className="impact-step-card"
    style={{
      borderRadius: 16,
      boxShadow: '0 6px 22px rgba(0,0,0,0.07)',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}
    bodyStyle={{ padding: '24px 24px 22px' }}
  >
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accent }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div
        style={{
          width: 38, height: 38, borderRadius: 12,
          background: `linear-gradient(135deg, ${PALETTE.ink} 0%, ${accent} 145%)`,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, flexShrink: 0, fontSize: 15,
          boxShadow: `0 4px 10px ${accent}33`,
        }}
      >
        {index}
      </div>
      <Title level={5} style={{ margin: 0 }}>{title}</Title>
    </div>
    {children}
  </Card>
);

const ImpactAnalytics = () => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [quickRange, setQuickRange] = useState('all');
  const [excludeArchived, setExcludeArchived] = useState(true);
  const [showEmptyNotice, setShowEmptyNotice] = useState(
    () => localStorage.getItem('bha_impact_emptyNoticeDismissed') !== '1'
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const dismissEmptyNotice = () => {
    setShowEmptyNotice(false);
    try { localStorage.setItem('bha_impact_emptyNoticeDismissed', '1'); } catch (e) { /* ignore */ }
  };

  const handleQuickRange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    switch (value) {
      case 'all': setDateRange(null); break;
      case '3m': setDateRange([now.subtract(3, 'month'), now]); break;
      case '6m': setDateRange([now.subtract(6, 'month'), now]); break;
      case '12m': setDateRange([now.subtract(12, 'month'), now]); break;
      case 'ytd': setDateRange([now.startOf('year'), now]); break;
      case 'lastYear': setDateRange([now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')]); break;
      default: break;
    }
  };

  useEffect(() => {
    setPageHeader({ title: 'Community Impact', count: null, countIcon: null });
  }, [setPageHeader]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      if (excludeArchived) params.excludeArchived = 'true';

      const res = await api.get('/screening/analytics/impact/roi', params);
      const payload = res?.data?.success !== undefined
        ? (res.data.success ? res.data.data : null)
        : (res?.data || res);
      setData(payload);
    } catch (err) {
      console.error('Error loading community impact data:', err);
      message.error('Failed to load community impact analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange, excludeArchived]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <HeartLoader />
        </div>
        <ImpactSettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSaved={loadData}
        />
      </PageContainer>
    );
  }

  const cohort = data?.cohort || {};
  const bp = data?.bloodPressure || {};
  const rr = data?.riskReduction || {};
  const cases = data?.casesAvoided || {};
  const ed = data?.emergencyDepartment || {};
  const savings = data?.savings || {};
  const roi = data?.roi || {};
  const a = data?.assumptions || {};

  const hasCohort = (cohort.highBaselineReturners || 0) > 0;

  // ── Before / after BP chart ───────────────────────────────────────────────
  const bpChart = {
    options: {
      chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
      plotOptions: { bar: { columnWidth: '55%', borderRadius: 6, dataLabels: { position: 'top' } } },
      colors: [PALETTE.slate, PALETTE.tiffany],
      dataLabels: {
        enabled: true,
        formatter: (v) => `${v}`,
        offsetY: -18,
        style: { colors: ['#595959'], fontSize: '12px' },
      },
      xaxis: { categories: ['Systolic', 'Diastolic'] },
      yaxis: { title: { text: 'mmHg' } },
      legend: { position: 'top' },
      grid: { borderColor: '#f0f0f0' },
    },
    series: [
      { name: 'First visit', data: [bp.baselineSystolic || 0, bp.baselineDiastolic || 0] },
      { name: 'Follow-up visits', data: [bp.followupSystolic || 0, bp.followupDiastolic || 0] },
    ],
  };

  // ── Savings breakdown chart ───────────────────────────────────────────────
  const savingsChart = {
    options: {
      chart: { type: 'donut', fontFamily: 'inherit' },
      labels: ['Heart attacks & strokes prevented', 'ER visits avoided'],
      colors: [PALETTE.tiffany, PALETTE.ink],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` },
      tooltip: { y: { formatter: (v) => usd(v) } },
    },
    series: [savings.bloodPressure || 0, savings.emergencyDepartment || 0],
  };

  // ── Care diversion breakdown ──────────────────────────────────────────────
  const careEntries = Object.entries(ed.breakdown || {}).filter(([, v]) => v > 0);
  const careChart = {
    options: {
      chart: { type: 'donut', fontFamily: 'inherit' },
      labels: careEntries.map(([k]) => CARE_LABELS[k] || k),
      colors: [PALETTE.deepTiffany, PALETTE.tiffany, PALETTE.brightTiffany, '#9bb7b4', PALETTE.slate, PALETTE.ink],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` },
    },
    series: careEntries.map(([, v]) => v),
  };

  return (
    <PageContainer>
      <style>{`
        .impact-step-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .impact-step-card:hover { transform: translateY(-3px); box-shadow: 0 12px 30px rgba(0,0,0,0.10); }
      `}</style>
      {/* Filters */}
      <FiltersContainer style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            Quick range{' '}
            <Tooltip title="This page is cumulative — 'All time' is recommended. Narrowing the window only counts visits inside it, which can exclude members whose follow-up happened earlier or later.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
          </Text>
          <Select
            value={quickRange}
            onChange={handleQuickRange}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'All time (recommended)' },
              { value: 'ytd', label: 'Year to date' },
              { value: '12m', label: 'Last 12 months' },
              { value: '6m', label: 'Last 6 months' },
              { value: '3m', label: 'Last 3 months' },
              { value: 'lastYear', label: 'Last year' },
              { value: 'custom', label: 'Custom…', disabled: true },
            ]}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Custom range</Text>
          <RangePicker
            value={dateRange}
            onChange={(dates) => { setDateRange(dates); setQuickRange(dates ? 'custom' : 'all'); }}
            allowClear
            style={{ minWidth: 240 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch checked={excludeArchived} onChange={setExcludeArchived} size="small" />
          <Text type="secondary" style={{ fontSize: 12 }}>Exclude archived members</Text>
        </div>
        <Button
          icon={<SettingOutlined />}
          onClick={() => setSettingsOpen(true)}
          style={{ marginLeft: 'auto' }}
        >
          Cost &amp; calculation settings
        </Button>
      </FiltersContainer>

      {!hasCohort && showEmptyNotice && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={dismissEmptyNotice}
          style={{ marginBottom: 20, borderRadius: 12 }}
          message="Not enough longitudinal data yet to tell the full story"
          description={
            <span>
              This analysis needs members who had <strong>high blood pressure at their first visit</strong> and
              returned for at least {a.minVisitsForReturner || 2} blood-pressure visits. As more members return
              for repeat screenings, this page will populate automatically.
            </span>
          }
        />
      )}

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{
          borderRadius: 20, marginBottom: 24, position: 'relative', overflow: 'hidden',
          background: PALETTE.heroGradient,
          color: '#fff',
          border: '1px solid rgba(78,205,196,0.18)',
        }}
        bodyStyle={{ padding: '36px 32px', position: 'relative', zIndex: 1 }}
      >
        {/* Tiffany glow accents */}
        <div style={{
          position: 'absolute', top: -120, right: -80, width: 360, height: 360, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(78,205,196,0.22) 0%, rgba(78,205,196,0) 70%)', zIndex: 0,
        }} />
        <div style={{
          position: 'absolute', bottom: -140, left: -60, width: 320, height: 320, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(78,205,196,0.10) 0%, rgba(78,205,196,0) 70%)', zIndex: 0,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Text style={{
            color: PALETTE.tiffany, letterSpacing: 2, fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
          }}>
            Community Impact Report
          </Text>
          <Title level={3} style={{ color: '#fff', margin: '6px 0 4px' }}>
            Turning blood pressure readings into lives and dollars saved
          </Title>
          <div style={{ width: 60, height: 3, background: PALETTE.tiffany, borderRadius: 2, margin: '10px 0 18px' }} />
          <Paragraph style={{ color: 'rgba(255,255,255,0.82)', maxWidth: 760, marginBottom: 28 }}>
            Every screening Black Hearts delivers does more than capture a number. For members who arrive with
            high blood pressure and come back, we can estimate the heart attacks and strokes prevented, the
            emergency-room visits avoided, and the dollars those outcomes save the health system.
          </Paragraph>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={8}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.75)' }}>Estimated total savings</span>}
                value={usd(savings.total)}
                valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                prefix={<DollarOutlined style={{ color: PALETTE.tiffany }} />}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.75)' }}>Heart attacks & strokes prevented</span>}
                value={num(cases.total, 1)}
                valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                prefix={<SafetyCertificateOutlined style={{ color: PALETTE.tiffany }} />}
              />
            </Col>
            <Col xs={24} sm={8}>
              {roi.ratio != null ? (
                <Statistic
                  title={<span style={{ color: 'rgba(255,255,255,0.75)' }}>Return on investment</span>}
                  value={`${num(roi.ratio, 2)}×`}
                  valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                  prefix={<RiseOutlined style={{ color: PALETTE.tiffany }} />}
                />
              ) : (
                <div>
                  <Text style={{ color: 'rgba(255,255,255,0.75)', display: 'block', marginBottom: 6 }}>
                    Return on investment
                  </Text>
                  <Text style={{ color: '#fff' }}>
                    Enter your program cost above to calculate ROI.
                  </Text>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Card>

      {/* ── STORY STEPS ───────────────────────────────────────────────────── */}
      <SectionTitle style={{ marginBottom: 16 }}>How we get there</SectionTitle>

      <Row gutter={[20, 20]} style={{ marginBottom: 4 }}>
        <Col xs={24} md={12} lg={8}>
          <StepCard index={1} title="Who we helped">
            <Statistic
              value={num(cohort.highBaselineReturners)}
              prefix={<TeamOutlined style={{ color: PALETTE.deepTiffany }} />}
              valueStyle={{ color: PALETTE.deepTiffany, fontWeight: 700 }}
              suffix="members"
            />
            <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
              Members who arrived with high blood pressure (≥{a.baselineHighSystolic || 140}/
              {a.baselineHighDiastolic || 90}) and returned for repeat screenings. They averaged{' '}
              <strong>{num(cohort.meanVisitsPerPatient, 1)} visits</strong> over{' '}
              <strong>{num(cohort.personYears, 0)} person-years</strong> of follow-up.
            </Paragraph>
          </StepCard>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <StepCard index={2} title="How much blood pressure fell">
            <Row gutter={12}>
              <Col span={12}>
                <Statistic
                  title="Systolic drop"
                  value={num(bp.systolicReduction, 1)}
                  suffix="mmHg"
                  prefix={<FallOutlined />}
                  valueStyle={{ color: '#108043', fontWeight: 700 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Diastolic drop"
                  value={num(bp.diastolicReduction, 1)}
                  suffix="mmHg"
                  prefix={<FallOutlined />}
                  valueStyle={{ color: '#108043', fontWeight: 700 }}
                />
              </Col>
            </Row>
            <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
              Average change from each member’s first visit to their follow-up visits.
            </Paragraph>
          </StepCard>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <StepCard index={3} title="What that means for risk">
            <Row gutter={12}>
              <Col span={12}>
                <Statistic
                  title="Heart attack risk"
                  value={num(rr.miPercent, 1)}
                  suffix="% lower"
                  valueStyle={{ color: PALETTE.deepTiffany, fontWeight: 700 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Stroke risk"
                  value={num(rr.strokePercent, 1)}
                  suffix="% lower"
                  valueStyle={{ color: PALETTE.deepTiffany, fontWeight: 700 }}
                />
              </Col>
            </Row>
            <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
              Relative risk reduction implied by the blood-pressure drop, using established clinical
              relationships.
            </Paragraph>
          </StepCard>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <StepCard index={4} title="Lives protected" accent={PALETTE.ink}>
            <Row gutter={12}>
              <Col span={12}>
                <Statistic
                  title="Heart attacks"
                  value={num(cases.mi, 2)}
                  prefix={<HeartOutlined style={{ color: PALETTE.ink }} />}
                  valueStyle={{ color: PALETTE.ink, fontWeight: 700 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Strokes"
                  value={num(cases.stroke, 2)}
                  prefix={<MedicineBoxOutlined style={{ color: PALETTE.ink }} />}
                  valueStyle={{ color: PALETTE.ink, fontWeight: 700 }}
                />
              </Col>
            </Row>
            <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
              Estimated cardiovascular events avoided across the cohort’s follow-up period.
            </Paragraph>
          </StepCard>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <StepCard index={5} title="Dollars saved" accent={PALETTE.green}>
            <Statistic
              value={usd(savings.total)}
              prefix={<DollarOutlined style={{ color: PALETTE.green }} />}
              valueStyle={{ color: PALETTE.green, fontWeight: 700 }}
            />
            <div style={{ marginTop: 10 }}>
              <Tag style={{ background: PALETTE.tiffanyTint, color: PALETTE.deepTiffany, border: 'none' }}>BP outcomes: {usd(savings.bloodPressure)}</Tag>
              <Tag style={{ marginTop: 6, background: '#f0f0f0', color: PALETTE.charcoal, border: 'none' }}>ER avoided: {usd(savings.emergencyDepartment)}</Tag>
            </div>
          </StepCard>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <StepCard index={6} title="Return on investment" accent={PALETTE.tiffany}>
            {roi.ratio != null ? (
              <>
                <Statistic
                  value={`${num(roi.ratio, 2)}×`}
                  prefix={<RiseOutlined style={{ color: PALETTE.deepTiffany }} />}
                  valueStyle={{ color: PALETTE.deepTiffany, fontWeight: 700 }}
                />
                <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
                  Every {usd(1)} invested returns an estimated {usd(roi.ratio)} in avoided
                  health-system costs (program cost {usd(roi.programCost)}).
                </Paragraph>
              </>
            ) : (
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Enter your program operating cost in the filter above to calculate the return-on-investment
                ratio.
              </Paragraph>
            )}
          </StepCard>
        </Col>
      </Row>

      <Divider />

      {/* ── CHARTS ────────────────────────────────────────────────────────── */}
      <Row gutter={[20, 20]} align="stretch" style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <ChartCard title="Blood pressure: first visit vs. follow-up" bordered={false} style={{ height: '100%' }}>
            <Chart options={bpChart.options} series={bpChart.series} type="bar" height={320} />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Where the savings come from" bordered={false} style={{ height: '100%' }}>
            {(savings.total || 0) > 0 ? (
              <Chart options={savingsChart.options} series={savingsChart.series} type="donut" height={320} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
                <Empty description="No savings to display yet" />
              </div>
            )}
          </ChartCard>
        </Col>
      </Row>

      {/* ── ER DIVERSION ──────────────────────────────────────────────────── */}
      <Row gutter={[20, 20]} align="stretch" style={{ marginBottom: 4 }}>
        <Col xs={24} lg={12}>
          <ChartCard
            title={
              <span>
                <ThunderboltOutlined style={{ color: PALETTE.deepTiffany, marginRight: 8 }} />
                Emergency-room diversion
              </span>
            }
            bordered={false}
            style={{ height: '100%' }}
          >
            {ed.captured ? (
              <>
                <Row gutter={12} style={{ marginBottom: 12 }}>
                  <Col span={12}>
                    <Statistic title="Members who said they'd have gone to the ER" value={num(ed.visitsAvoided)} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Total responses" value={num(ed.respondents)} />
                  </Col>
                </Row>
                {careEntries.length > 0 && (
                  <Chart options={careChart.options} series={careChart.series} type="donut" height={260} />
                )}
              </>
            ) : (
              <Alert
                type="warning"
                showIcon
                message="This question isn't being collected yet"
                description={
                  <span>
                    The emergency-room savings are driven by a short intake question — “If this screening were
                    not available today, where would you have gone for care?” Enable the{' '}
                    <strong>Care Alternative</strong> field in <strong>Form Config</strong> to start capturing
                    it; this card and the ER portion of savings will populate automatically.
                  </span>
                }
              />
            )}
          </ChartCard>
        </Col>

        {/* ── METHODOLOGY ─────────────────────────────────────────────────── */}
        <Col xs={24} lg={12}>
          <ChartCard title="How this is calculated" bordered={false} style={{ height: '100%' }}>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              We compare each high-baseline member’s first-visit blood pressure to their follow-up average,
              convert that reduction into lower heart-attack and stroke risk, translate the risk reduction into
              cases and dollars avoided, and add savings from avoided ER visits. All assumptions below are
              transparent and adjustable.
            </Paragraph>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
              }}
            >
              {[
                { label: 'Cost per heart attack', value: usd(a.costPerMI) },
                { label: 'Cost per stroke', value: usd(a.costPerStroke) },
                { label: 'Cost per avoided ER visit', value: usd(a.costPerEdVisit) },
                { label: 'Baseline incidence band', value: cohort.incidenceBracket || 'n/a' },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 10,
                    padding: '12px 14px',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                    {item.label}
                  </Text>
                  <Text strong style={{ fontSize: 16, color: PALETTE.charcoal }}>{item.value}</Text>
                </div>
              ))}
            </div>
            <Divider style={{ margin: '16px 0' }} />
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              Methodology adapted from {data?.meta?.citation}{' '}
              <a
                href={STUDY_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: PALETTE.deepTiffany, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                <FilePdfOutlined /> View the study (PDF)
              </a>
            </Paragraph>
          </ChartCard>
        </Col>
      </Row>

      <ImpactSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={loadData}
      />
    </PageContainer>
  );
};

export default ImpactAnalytics;
